import * as React from 'react';
import { ClientItemId } from 'owa-client-ids';
import { FreeBusyType } from 'owa-freebusy-style';
import { AgendaFreeBusyBar } from './AgendaFreeBusyBar';
import ColorScheme from 'owa-color-utils/lib/ColorScheme';
import { Detail, TextDetailPart, ButtonDetailPart } from '../schema/DetailPart';
import { DetailPartView, ButtonPartView } from './AgendaEventDetailViews';
import { IIconProps } from 'office-ui-fabric-react/lib/Icon';
import { IconWell } from './IconWell';
import { FocusZone, FocusZoneDirection, IFocusZone } from 'office-ui-fabric-react/lib/FocusZone';
import { KeyboardEvent } from 'react';
import { getRTLSafeKeyCode } from '@uifabric/utilities';
import { KeyCodes } from 'office-ui-fabric-react/lib/Utilities';

const styles = require('./AgendaEventViewBase.scss');
const classNames = require('classnames/bind').bind(styles);

export interface AgendaEventViewBaseProps {
    id: ClientItemId;
    onClick: (target: any) => void;
    onDoubleClick: (target: any) => void;
    onRightClick: (target: any) => void;
    className?: string;
    timeDetails: TextDetailPart[];
    details: Detail[];
    freeBusyType: FreeBusyType;
    colorScheme: ColorScheme;
    icons: IIconProps[];
    isSelected: boolean;
    buttonDetail?: ButtonDetailPart;
}

export class AgendaEventViewBase extends React.Component<AgendaEventViewBaseProps, {}> {
    private focusZoneRef: React.RefObject<IFocusZone> = React.createRef();

    render() {
        const {
            className,
            timeDetails,
            details,
            icons,
            freeBusyType,
            colorScheme,
            isSelected,
            buttonDetail,
        } = this.props;
        return (
            <FocusZone
                direction={FocusZoneDirection.horizontal}
                componentRef={this.focusZoneRef}
                data-is-focusable={true}
                onClick={this.onClick}
                onDoubleClick={this.onDoubleClick}
                onContextMenu={this.onRightClick}
                className={classNames(
                    styles.agendaItem,
                    {
                        selected: isSelected,
                    },
                    className
                )}
                onKeyDown={this.onKeyDown}>
                <AgendaFreeBusyBar color={colorScheme} freeBusyType={freeBusyType} />
                <div className={styles.base}>
                    {this.getDetailRows(details, timeDetails, icons, buttonDetail)}
                </div>
            </FocusZone>
        );
    }

    private getDetailRows = (
        details: Detail[],
        timeDetails: TextDetailPart[],
        icons: IIconProps[],
        buttonDetail: ButtonDetailPart
    ): JSX.Element[] => {
        const longerDetailsLength = Math.max(details.length, timeDetails.length);
        const detailElements = [];
        /** align the icons with the last detail, unless the button occupies this space, then align the icons with the last time detail
         * (this happens when there is only 1 detail, and a button)
         */
        const iconRowIndex =
            buttonDetail && details.length === 1 ? timeDetails.length - 1 : details.length - 1;
        for (let index = 0; index < longerDetailsLength; index++) {
            detailElements.push(
                <DetailRow
                    key={index}
                    timeDetail={timeDetails.length > index ? timeDetails[index] : null}
                    detail={details.length > index ? details[index] : null}
                    buttonDetail={index === 0 ? buttonDetail : null}
                    icons={index === iconRowIndex ? icons : null}
                />
            );
        }
        return detailElements;
    };

    private onKeyDown = (target: KeyboardEvent<EventTarget>) => {
        switch (target.keyCode) {
            case getRTLSafeKeyCode(KeyCodes.right):
                this.focusZoneRef.current.focus(true);
                target.preventDefault();
                target.stopPropagation();
                break;
        }
    };

    private onClick = evt => {
        this.props.onClick(evt);
    };

    private onDoubleClick = evt => {
        this.props.onDoubleClick(evt);
    };

    private onRightClick = evt => {
        this.props.onRightClick(evt);
    };
}

const DetailRow = function DetailRow(props: {
    detail: Detail | null;
    timeDetail: TextDetailPart | null;
    icons: IIconProps[] | null;
    buttonDetail: ButtonDetailPart | null;
}) {
    return (
        <div className={styles.detailRow}>
            <div className={styles.timesContainer}>
                {props.timeDetail && (
                    <div
                        title={props.timeDetail.getText()}
                        className={classNames(props.timeDetail.className, styles.timeDetail)}>
                        {props.timeDetail.getText()}
                    </div>
                )}
            </div>
            {props.detail && <DetailPartView {...props.detail} />}
            {props.buttonDetail && <ButtonPartView {...props.buttonDetail} />}
            {props.icons && props.icons.length > 0 && <IconWell icons={props.icons} />}
        </div>
    );
};
